import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FloatingBackToTop from '@/components/feature/FloatingBackToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { products, categories } from '@/mocks/products';

const tabs = [
  { id: 'all', icon: 'ri-store-line' },
  ...categories.map((c) => ({ id: c.id, icon: c.icon })),
];

const allLabel = '全部';

const categoryLabels: Record<string, string> = {
  all: '全部',
  beauty: '美妆护肤',
  food: '食品零食',
  home: '家居日用',
  health: '健康保健',
  fashion: '服饰配饰',
  baby: '母婴用品',
  digital: '数码电器',
};

function CategoryContent() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';
  const [sortBy, setSortBy] = useState('newest');
  const [showSortMenu, setShowSortMenu] = useState(false);

  const filteredProducts = activeTab === 'all'
    ? products
    : products.filter((p) => p.categoryId === activeTab);

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'popular') return (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0);
    if (sortBy === 'newest') return (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0);
    return 0;
  });

  const handleTabChange = useCallback((tabId: string) => {
    setSearchParams({ tab: tabId });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setSearchParams]);

  const sortLabels: Record<string, string> = {
    newest: t('sortNewest'),
    popular: t('sortPopular'),
    priceLow: t('sortPriceLow'),
    priceHigh: t('sortPriceHigh'),
  };

  return (
    <>
      <SEOHead
        title="全品类特惠专区 - 岛国精品｜日本原装进口商品"
        description="浏览岛国精品全部分类商品，涵盖美妆护肤、食品零食、家居日用、健康保健等多品类日本原装进口好物。品质严选，正品保障。"
        keywords="岛国精品全品类,日本进口商品分类,岛国精品美妆护肤,日本零食,日本家居用品"
        canonicalUrl="https://www.daoguojingpin.com/category"
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.daoguojingpin.com' },
          { '@type': 'ListItem', position: 2, name: '全品类专区', item: 'https://www.daoguojingpin.com/category' },
        ],
      }} />

      <SchemaOrg type="WebPage" data={{
        name: '全品类特惠专区 - 岛国精品',
        url: 'https://www.daoguojingpin.com/category',
        description: '浏览岛国精品全部分类商品',
        inLanguage: 'zh-CN',
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 md:pt-20">
          <Breadcrumb items={[{ label: t('pageTitle') }]} />

          <section className="w-full px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
                {t('pageTitle')}
              </h1>
              <p className="text-foreground-500 text-sm mb-8">
                {t('pageDescription')}
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 w-full sm:w-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-300 ${
                        activeTab === tab.id
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      <i className={`${tab.icon} text-sm`}></i>
                      {categoryLabels[tab.id] || tab.id}
                    </button>
                  ))}
                </div>

                <div className="relative">
                  <button
                    onClick={() => setShowSortMenu(!showSortMenu)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors duration-200 whitespace-nowrap"
                  >
                    <i className="ri-arrow-up-down-line text-sm"></i>
                    {sortLabels[sortBy]}
                  </button>
                  {showSortMenu && (
                    <div className="absolute right-0 top-full mt-2 bg-background-50 rounded-lg border border-background-200 shadow-lg z-20 min-w-[140px] py-1">
                      {Object.entries(sortLabels).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => { setSortBy(key); setShowSortMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-xs hover:bg-background-100 transition-colors duration-150 whitespace-nowrap ${
                            sortBy === key ? 'text-primary-600 font-medium' : 'text-foreground-600'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {sortedProducts.length > 0 ? (
                <>
                  <p className="text-xs text-foreground-400 mb-5">
                    {sortedProducts.length} {t('productCount')}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-product-shop>
                    {sortedProducts.map((product) => (
                      <div
                        key={product.id}
                        className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200/70 transition-all duration-500 cursor-pointer"
                      >
                        <div className="aspect-square overflow-hidden">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            {product.isNew && (
                              <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">新品</span>
                            )}
                            <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                              {categoryLabels[product.categoryId]}
                            </span>
                          </div>
                          <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5 line-clamp-1">
                            {product.name}
                          </h3>
                          <p className="text-xs text-foreground-500 mb-3 line-clamp-2 leading-relaxed">
                            {product.description}
                          </p>
                          <p className="text-base font-bold text-primary-600">
                            {product.price}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">{t('emptyProducts')}</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
        <FloatingBackToTop />
      </div>
    </>
  );
}

export default function CategoryPage() {
  return (
    <>
      <ScrollToTop />
      <CategoryContent />
    </>
  );
}